import { useEffect, useMemo, useRef, useState } from 'react'
import { useCatalog } from '../../contexts/CatalogContext'
import { useCart, useCartActions, useOverlayActions, useOverlays } from '../../contexts/StoreContext'
import { useAccount } from '../../contexts/AccountContext'
import { useSite } from '../../contexts/SiteContext'
import { useFeatures } from '../../hooks/useFeatures'
import { canSubscribe } from '../../core/rules/features'
import { isSoldOut } from '../../core/rules/stock'
import { groupCart } from '../../core/rules/cartBundles'
import { giftDeliveryIn } from '../../core/rules/subscriptionBuilder'
import { boxRange, planBoxes, workingCadences } from '../../core/rules/refillPlan'
import { refillItemsFromProducts, refillRules } from '../../utils/refill'
import { shiftDate, today } from '../../core/format/dates'
import type { Product, Routine, SkinType } from '../../core/types/catalog'

export type BuilderTab = 'protocol' | 'products'

/** Hoeveel leveringen de samensteller vooruit laat zien. */
const PREVIEW_COUNT = 3
/** Hoeveel huidtype-chips er hoogstens staan; de rest is via zoeken in de webshop. */
const MAX_SKIN_CHIPS = 5

/**
 * De abonnementsamensteller: wat je bijvult (een protocol of losse producten), hoe vaak, en hoe de
 * eerste leveringen eruitzien. Bij openen staat er wat al als bijvullen in de mand ligt.
 */
export function useSubscriptionBuilder() {
  const { builderOpen } = useOverlays()
  const { closeSubscriptionBuilder } = useOverlayActions()
  const { addSubscription } = useCartActions()
  const cart = useCart()
  const { products, routines, routineTotals, productById } = useCatalog()
  const { account } = useAccount()
  const { policy } = useSite()
  const features = useFeatures()
  const { defaultIntervalWeeks, leadDays, giftEvery } = policy.subscription

  const refillable = useMemo(() => products.filter((p) => canSubscribe(features, p) && !isSoldOut(p)), [products, features])
  const routineOptions = useMemo(
    () => routines.filter((r) => routineTotals(r).items.some((p) => canSubscribe(features, p) && !isSoldOut(p))),
    [routines, routineTotals, features],
  )
  /** Uit het huidplan in Mijn omgeving: die staan bovenaan, met "Uit je huidplan". */
  const planIds = useMemo(() => new Set(account?.plan.items.map((i) => i.productId) ?? []), [account])

  const [tab, setTab] = useState<BuilderTab>('protocol')
  const [routineId, setRoutineId] = useState<string | null>(null)
  const [qty, setQty] = useState<Record<string, number>>({})
  const [skin, setSkin] = useState<SkinType | null>(null)
  const [chosenInterval, setChosenInterval] = useState<number | null>(null)

  // Bij openen: begin bij wat er al als bijvullen in de mand ligt.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (builderOpen && !wasOpen.current) {
      const groups = groupCart(cart.lines)
      const bundle = groups.find((g) => g.kind === 'bundle' && g.lines.some((l) => l.subscribe))
      const loose = cart.lines.filter((l) => l.subscribe && !(bundle?.kind === 'bundle' && bundle.lines.includes(l)))
      setRoutineId(bundle?.kind === 'bundle' ? bundle.routineId : null)
      setQty(Object.fromEntries(loose.map((l) => [l.product.id, l.qty])))
      setTab(bundle || !loose.length ? 'protocol' : 'products')
      setChosenInterval(cart.hasSubscription ? cart.subscriptionIntervalWeeks : null)
      setSkin(null)
    }
    wasOpen.current = builderOpen
  }, [builderOpen, cart])

  const routine: Routine | undefined = routineId ? routineOptions.find((r) => r.id === routineId) : undefined
  const routineItems = routine ? routineTotals(routine).items.filter((p) => canSubscribe(features, p) && !isSoldOut(p)) : []
  const looseItems = Object.entries(qty)
    .map(([id, n]) => ({ product: productById(id), qty: n }))
    .filter((i): i is { product: Product; qty: number } => Boolean(i.product) && i.qty > 0 && !routineItems.some((p) => p.id === i.product?.id))

  const chips = useMemo(() => {
    const count = new Map<SkinType, number>()
    refillable.forEach((p) => p.skinTypes.forEach((s) => count.set(s, (count.get(s) ?? 0) + 1)))
    return [...count.entries()].sort((a, b) => b[1] - a[1]).slice(0, MAX_SKIN_CHIPS).map(([s]) => s)
  }, [refillable])

  const productList = useMemo(() => {
    const shown = skin ? refillable.filter((p) => p.skinTypes.includes(skin)) : refillable
    return [...shown].sort((a, b) => Number(planIds.has(b.id)) - Number(planIds.has(a.id)))
  }, [refillable, skin, planIds])

  const everything = [...routineItems, ...looseItems.map((i) => i.product)]
  // Slim bijvullen: de eerste bestelling is alles; daarna per doos alleen wat op raakt.
  const start = today()
  const rules = refillRules(policy.subscription)
  const refillItems = refillItemsFromProducts([...routineItems.map((product) => ({ product })), ...looseItems], start)
  const working = workingCadences(refillItems, rules, shiftDate(start, leadDays))
  const suggested = working[0] ?? null
  const intervalWeeks = chosenInterval !== null && working.includes(chosenInterval) ? chosenInterval : (suggested ?? defaultIntervalWeeks)
  const firstOrder = routineItems.reduce((n, p) => n + p.price, 0) + looseItems.reduce((n, i) => n + i.product.price * i.qty, 0)
  const boxes = suggested === null ? [] : planBoxes(refillItems, { intervalWeeks, rules, earliest: shiftDate(start, leadDays), count: PREVIEW_COUNT })
  const range = boxRange(boxes)
  const empty = everything.length === 0
  /** Gekozen, maar geen enkel ritme haalt het minimum per doos. */
  const tooSmall = !empty && suggested === null

  return {
    open: builderOpen,
    close: closeSubscriptionBuilder,
    tab,
    setTab,
    routineOptions,
    routineId: routine?.id ?? null,
    toggleRoutine: (id: string) => setRoutineId((cur) => (cur === id ? null : id)),
    productList,
    planIds,
    qty,
    setProductQty: (id: string, n: number) => setQty((q) => ({ ...q, [id]: Math.max(0, Math.min(n, 9)) })),
    chips,
    skin,
    setSkin,
    intervals: working,
    intervalWeeks,
    suggested,
    chooseInterval: setChosenInterval,
    boxes,
    range,
    minBoxValue: rules.minBoxValue,
    giftAt: giftDeliveryIn(giftEvery, PREVIEW_COUNT),
    firstOrder,
    count: everything.length,
    empty,
    tooSmall,
    submit: () => {
      if (empty || tooSmall) return
      addSubscription({ routineId: routine?.id, items: looseItems, intervalWeeks })
    },
  }
}

export type SubscriptionBuilderControl = ReturnType<typeof useSubscriptionBuilder>
